import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { api, ApiError } from '@forbiz/web-kit'
import { passwordResetRequestSchema } from '@forbiz/contracts'

import styles from './InviteAcceptPage.module.css'

/**
 * 비밀번호 재설정 요청 화면(공개 경로 /forgot-password, BIZ-55).
 *
 * 이메일을 받아 재설정 링크를 요청한다. 계정 존재 여부를 드러내지 않으려
 * 서버가 항상 성공을 돌려주므로, 화면도 결과와 무관하게 동일한 안내를 보인다.
 * 링크는 알림톡으로 발송(발송기는 BIZ-54).
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setFieldError('')
    setFormError('')

    const parsed = passwordResetRequestSchema.safeParse({ email })
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? '이메일을 확인하세요')
      return
    }

    setSubmitting(true)
    try {
      await api.post('/api/auth/password-reset/request', parsed.data)
      setDone(true)
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : '일시적인 오류가 발생했습니다')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.brand}>포비즈</h1>
        <p className={styles.subtitle}>비밀번호 재설정</p>

        {done ? (
          <div role="status">
            <p className={styles.status}>
              요청을 접수했습니다. 등록된 계정이면 알림톡으로 재설정 링크를 보내드립니다.
            </p>
            <div className={styles.footer}>
              <Link to="/login">로그인 화면으로</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <p className={styles.errorBody} style={{ marginBottom: 'var(--space-md)' }}>
              가입한 이메일을 입력하면, 등록된 번호로 재설정 링크를 보내드립니다.
            </p>

            {formError && (
              <div className={styles.formError} role="alert">
                {formError}
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="email">
                이메일
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={fieldError ? true : undefined}
                disabled={submitting}
              />
              {fieldError && <p className={styles.fieldError}>{fieldError}</p>}
            </div>

            <button type="submit" data-variant="primary" className={styles.submit} disabled={submitting}>
              {submitting ? '요청 중…' : '재설정 링크 받기'}
            </button>

            <div className={styles.footer}>
              <Link to="/login">로그인 화면으로</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
